import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, UserMinus, UserPlus, Users } from "lucide-react";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { useToast } from "../../context/ToastContext.js";
import { CheckboxList } from "../../components/ui/checkbox-list.js";
import { Button } from "../../components/ui/button.js";
import { Input } from "../../components/ui/input.js";
import { Textarea } from "../../components/ui/textarea.js";
import { Badge } from "../../components/ui/badge.js";
import { Pagination, type PaginationMeta } from "../../components/ui/pagination.js";
import { DropdownSelect } from "../../components/DropdownButton.js";
import { SearchInput } from "../../components/ui/search-input.js";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog.js";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table.js";
import {
  AssignmentStatusBadge, DeleteIconButton, EditIconButton, EmptyState, Field, GoalScopeBadge, SectionCard,
} from "./shared.js";
import { useConfirm } from "../../components/ui/confirm-dialog.js";
import { useMe } from "../../context/MeContext.js";
import { GOAL_SCOPE_LABELS, type GoalScope, type OrgGoal } from "./types.js";

interface EmployeeOption {
  id: string;
  firstName: string;
  lastName: string;
  employeeCode: string;
  role?: string;
  managerId?: string | null;
  reportingManagerId?: string | null;
}

// The goal type is only a label. Ownership is who created the goal: a manager
// owns the goals they create; goals HR creates have no owner.

// Rows per page, matching the Expenses list.
const PAGE_SIZE = 20;

// The organization, team and individual goals catalog that feeds each
// review's "Assigned goals" section. What a viewer sees is decided on the
// server: HR sees everything; a manager sees every organization goal plus the
// team and individual goals they own; anyone else only their own goals.
// `isHR` comes from the page, from the same token decode that decides
// `canEdit`, so the two can never disagree. useMe().role is only filled once
// /me/profile resolves and can be blank on first render.
const GOAL_TYPE_OPTIONS = [
  { value: "ALL", label: "All types" },
  ...(Object.keys(GOAL_SCOPE_LABELS) as GoalScope[]).map((t) => ({ value: t, label: GOAL_SCOPE_LABELS[t] })),
];

export default function OrgGoalsPanel({ canEdit, isHR }: { canEdit: boolean; isHR: boolean }) {
  const toast = useToast();
  const [goals, setGoals] = useState<OrgGoal[]>([]);
  const [years, setYears] = useState<{ value: number; label: string }[]>([]);
  const [fiscalYear, setFiscalYear] = useState("");
  // "ALL" or one goal type; the server filters on it like the fiscal year.
  const [openToolbar, setOpenToolbar] = useState<string | null>(null);
  const [goalType, setGoalType] = useState<"ALL" | GoalScope>("ALL");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const confirm = useConfirm();
  const me = useMe();
  // Mirrors the server's assertCanMutateGoal: whoever created a goal changes it.
  // HR-created goals have no owner and are HR's. Other rows show no buttons.
  const canMutate = (g: OrgGoal) =>
    canEdit && (g.ownerManagerId === me.employeeId || (isHR && g.ownerManagerId === null));
  // Mirrors assertCanAssignGoal: on top of that, a manager may pass on a goal
  // assigned to them to their own team.
  const canAssign = (g: OrgGoal) =>
    canMutate(g) || (canEdit && g.assignments.some((a) => a.employeeId === me.employeeId));
  // Unassigning follows the server's removeAssignment: HR and the goal's owner
  // may remove anyone; a manager passing a goal on may remove their own reports,
  // but not their own assignment, which came from above.
  const canUnassign = (g: OrgGoal, employeeId: string) =>
    canMutate(g) || (canAssign(g) && employeeId !== me.employeeId);
  const [assignToId, setAssignToId] = useState<string | null>(null);
  const [viewAssigneesId, setViewAssigneesId] = useState<string | null>(null);
  const assignTo = goals.find((g) => g.id === assignToId) ?? null;
  const viewAssignees = goals.find((g) => g.id === viewAssigneesId) ?? null;
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState<PaginationMeta>({ total: 0, page: 1, limit: PAGE_SIZE, totalPages: 0 });

  useEffect(() => {
    api
      .get<{ fiscalYears: { value: number; label: string }[] }>("/performance/meta")
      .then((r) => {
        setYears(r.data.fiscalYears);
        const first = r.data.fiscalYears[0];
        if (first) setFiscalYear(String(first.value));
      })
      .catch((e) => logError("OrgGoalsPanel.meta", e));
  }, []);

  // Search runs on the server, so wait for a pause in typing before asking —
  // the same 300ms debounce the Expenses list uses.
  useEffect(() => {
    const next = search.trim();
    if (next === debouncedSearch) return;
    const t = setTimeout(() => {
      // Both updates land in one render, so no request pairs the new search
      // with the old page number.
      setDebouncedSearch(next);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search, debouncedSearch]);

  // Only the newest request may update the table; a slower, older one that
  // resolves afterwards is dropped rather than overwriting fresher results.
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    if (!fiscalYear) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        fiscalYear,
        includeInactive: "true",
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (goalType !== "ALL") params.set("scope", goalType);
      const res = await api.get<{ data: OrgGoal[]; meta: PaginationMeta }>(`/org-goals?${params.toString()}`);
      if (seq !== requestSeq.current) return;
      // Deleting the last goal on the last page would otherwise strand the user
      // on an empty page; step back one instead.
      if (res.data.data.length === 0 && page > 1) {
        setPage(page - 1);
        return;
      }
      setGoals(res.data.data);
      setMeta(res.data.meta);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      logError("OrgGoalsPanel.load", e);
      toast.show(errMsg(e) ?? "Could not load goals", "error");
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [fiscalYear, goalType, page, debouncedSearch, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function deleteGoal(goal: OrgGoal) {
    const ok = await confirm({
      title: "Delete this goal?",
      description: `"${goal.title}" will be removed permanently. Nobody is assigned it, so no review is affected.`,
      actionText: "Delete",
      tone: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/org-goals/${goal.id}`);
      toast.show("Goal deleted", "success");
      void load();
    } catch (e) {
      logError("OrgGoalsPanel.delete", e);
      toast.show(errMsg(e) ?? "Could not delete the goal", "error");
    }
  }

  const editingGoal = goals.find((g) => g.id === editingId) ?? null;

  // New goals sort first, so after creating one jump to page 1 to show it.
  function onGoalCreated() {
    if (page === 1) void load();
    else setPage(1);
  }

  const noGoalsThisYear = !loading && meta.total === 0 && !debouncedSearch && goalType === "ALL";

  return (
    <SectionCard
      // The page header already says "Goals"; the search takes the
      // card header's left side instead. It stays mounted while results load,
      // so typing never loses focus when each debounced request comes back.
      leading={
        <>
          <SearchInput
            className="w-72"
            placeholder="Search goals or assignees"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {!noGoalsThisYear && (
            <span className="text-[12px] text-surface-500">
              {meta.total} goal{meta.total === 1 ? "" : "s"}
              {debouncedSearch || goalType !== "ALL" ? " match" : ""}
            </span>
          )}
        </>
      }
      actions={
        <>
          <DropdownSelect
            value={goalType}
            onChange={(v) => { setGoalType(v as "ALL" | GoalScope); setPage(1); }}
            options={GOAL_TYPE_OPTIONS}
            placeholder="All types"
            ariaLabel="Goal type"
            clearable={false}
            className="w-40 justify-between"
            open={openToolbar === "goalType"}
            onToggle={() => setOpenToolbar((d) => (d === "goalType" ? null : "goalType"))}
            onClose={() => setOpenToolbar((d) => (d === "goalType" ? null : d))}
          />
          <DropdownSelect
            value={fiscalYear}
            onChange={(v) => { setFiscalYear(v); setPage(1); }}
            options={years.map((y) => ({ value: String(y.value), label: y.label }))}
            placeholder="Fiscal year"
            ariaLabel="Fiscal year"
            clearable={false}
            className="w-40 justify-between"
            open={openToolbar === "fiscalYear"}
            onToggle={() => setOpenToolbar((d) => (d === "fiscalYear" ? null : "fiscalYear"))}
            onClose={() => setOpenToolbar((d) => (d === "fiscalYear" ? null : d))}
          />
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => setShowCreate(true)}>
              <Plus size={14} /> New goal
            </Button>
          )}
        </>
      }
    >
      {noGoalsThisYear ? (
        <EmptyState
          message="No goals for this fiscal year yet."
          action={
            canEdit ? (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                <Plus size={14} /> Create the first goal
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border border-surface-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 pl-3">#</TableHead>
                  <TableHead>Goal</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Created by</TableHead>
                  <TableHead>Assigned to</TableHead>
                  {canEdit && <TableHead className="text-center">Assign</TableHead>}
                  {canEdit && <TableHead className="pr-3 text-center">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 7 : 5} className="py-8 text-center text-[13px] text-surface-400">
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : goals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 7 : 5} className="py-8 text-center text-[13px] text-surface-400">
                      {debouncedSearch ? "No goals match your search." : "No goals of this type for this fiscal year."}
                    </TableCell>
                  </TableRow>
                ) : (
                  goals.map((g, i) => (
                    <TableRow key={g.id} className="align-top hover:bg-surface-50">
                      <TableCell className="pl-3 text-surface-400 tabular-nums">
                        {(meta.page - 1) * meta.limit + i + 1}
                      </TableCell>
                      <TableCell className="max-w-sm">
                        <div className="font-medium">{g.title}</div>
                        {g.description && (
                          <div className="line-clamp-2 text-[12px] text-surface-500" title={g.description}>
                            {g.description}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <GoalScopeBadge scope={g.scope} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{g.createdByName ?? "—"}</TableCell>
                      <TableCell>
                        <AssigneeCount goal={g} onOpen={() => setViewAssigneesId(g.id)} />
                      </TableCell>
                      {canEdit && (
                        <TableCell className="text-center">
                          {canAssign(g) ? (
                            <Button variant="outline" size="sm" onClick={() => setAssignToId(g.id)}>
                              <UserPlus size={13} /> Assign
                            </Button>
                          ) : (
                            <span className="text-[12px] text-surface-400">—</span>
                          )}
                        </TableCell>
                      )}
                      {canEdit && (
                        <TableCell className="pr-3 text-center">
                          {canMutate(g) ? (
                          <div className="flex items-center justify-center gap-0.5">
                            <EditIconButton name={g.title} onClick={() => setEditingId(g.id)} />
                            <DeleteIconButton
                              name={g.title}
                              onClick={() => void deleteGoal(g)}
                              blockedReason={
                                g.assignments.length > 0
                                  ? `Assigned to ${g.assignments.length} employee${g.assignments.length === 1 ? "" : "s"} — unassign everyone first`
                                  : undefined
                              }
                            />
                          </div>
                          ) : (
                            <span className="text-[12px] text-surface-400">—</span>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {!loading && <Pagination meta={meta} onPageChange={setPage} />}
        </>
      )}

      <GoalDialog
        open={showCreate}
        isHR={isHR}
        fiscalYear={fiscalYear}
        onClose={() => setShowCreate(false)}
        onSaved={onGoalCreated}
      />
      <GoalDialog
        open={editingGoal != null}
        goal={editingGoal}
        isHR={isHR}
        fiscalYear={fiscalYear}
        onClose={() => setEditingId(null)}
        onSaved={() => void load()}
      />
      <AssignDialog goal={assignTo} isHR={isHR} onClose={() => setAssignToId(null)} onSaved={load} />
      <AssigneesDialog
        goal={viewAssignees}
        canEdit={viewAssignees != null && canAssign(viewAssignees)}
        canUnassign={(employeeId) => viewAssignees != null && canUnassign(viewAssignees, employeeId)}
        onClose={() => setViewAssigneesId(null)}
        onAssignMore={() => {
          setAssignToId(viewAssigneesId);
          setViewAssigneesId(null);
        }}
        onChanged={load}
      />
    </SectionCard>
  );
}

// Create a goal, or edit one when `goal` is given. The goal type is a label
// shown as a badge. HR picks any of the three; a manager picks Team or
// Individual, and becomes the goal's owner (set by the server).
function GoalDialog({
  open,
  goal,
  isHR,
  fiscalYear,
  onClose,
  onSaved,
}: {
  open: boolean;
  goal?: OrgGoal | null;
  isHR: boolean;
  fiscalYear: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [scope, setScope] = useState<GoalScope>("ORGANIZATION");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const editing = !!goal;

  // Organization is HR's label; a manager chooses between the other two.
  const scopeChoices: GoalScope[] = isHR ? ["ORGANIZATION", "TEAM", "INDIVIDUAL"] : ["TEAM", "INDIVIDUAL"];

  useEffect(() => {
    if (!open) return;
    setTitle(goal?.title ?? "");
    setDescription(goal?.description ?? "");
    const current = goal?.scope;
    setScope(current && (isHR || current !== "ORGANIZATION") ? current : isHR ? "ORGANIZATION" : "TEAM");
    setError("");
  }, [open, goal, isHR]);

  async function save() {
    setSaving(true);
    setError("");
    const payload = { title: title.trim(), description: description || null, scope };
    try {
      if (goal) {
        await api.patch(`/org-goals/${goal.id}`, payload);
        toast.show("Goal updated", "success");
      } else {
        await api.post("/org-goals", { fiscalYear: Number(fiscalYear), ...payload });
        toast.show("Goal created", "success");
      }
      onSaved();
      onClose();
    } catch (e) {
      logError("OrgGoalsPanel.saveGoal", e);
      setError(errMsg(e) ?? "Could not save the goal");
    } finally {
      setSaving(false);
    }
  }

  const ready = title.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? "Edit goal" : "New goal"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <Field label="Title">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field label="Description">
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>

          <Field label="Goal type">
            <DropdownSelect
              value={scope}
              onChange={(v) => setScope(v as GoalScope)}
              options={scopeChoices.map((s) => ({ value: s, label: GOAL_SCOPE_LABELS[s] }))}
              placeholder="Goal type"
              ariaLabel="Goal type"
              clearable={false}
              className="w-full justify-between"
              open={openDropdown === "scope"}
              onToggle={() => setOpenDropdown((d) => (d === "scope" ? null : "scope"))}
              onClose={() => setOpenDropdown((d) => (d === "scope" ? null : d))}
            />
          </Field>

          {editing && goal && goal.assignments.length > 0 && (
            <p className="text-[12px] text-surface-500">
              Assigned to {goal.assignments.length} employee{goal.assignments.length === 1 ? "" : "s"}; they will see
              the updated wording on their reviews.
            </p>
          )}
          {error && <p className="text-[13px] text-[var(--color-error-fg)]">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={saving || !ready} onClick={() => void save()}>
            {saving ? "Saving…" : editing ? "Save changes" : "Create goal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({
  goal,
  isHR,
  onClose,
  onSaved,
}: {
  goal: OrgGoal | null;
  isHR: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const me = useMe();
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const goalId = goal?.id ?? null;
  useEffect(() => {
    if (!goalId) return;
    setError("");
    api
      .get<{ data: EmployeeOption[] }>("/employees?limit=500")
      // HR may assign anyone; a manager only their team — people whose manager or
      // reporting manager is them, which is what the server accepts.
      .then((r) =>
        setEmployees(
          isHR
            ? r.data.data
            : r.data.data.filter((e) => e.managerId === me.employeeId || e.reportingManagerId === me.employeeId),
        ),
      )
      .catch((e) => logError("OrgGoalsPanel.employees", e));
  }, [goalId, isHR, me.employeeId]);

  // The list is the goal's whole audience: people who already have it start
  // ticked, and unticking them unassigns them on save. Every assignment is the
  // person's own goal — the "assign as a manager" flavour was removed.
  const assignedIds = (goal?.assignments ?? [])
    .filter((a) => !a.asManager)
    .map((a) => a.employeeId);
  const assignedKey = assignedIds.join(",");
  useEffect(() => {
    setSelected(new Set(assignedKey ? assignedKey.split(",") : []));
  }, [goalId, assignedKey]);

  const listed = new Set(employees.map((e) => e.id));
  const added = Array.from(selected).filter((id) => !assignedIds.includes(id)).length;
  // Only people in the picker can be unticked; anyone else on the goal is left alone.
  const removed = assignedIds.filter((id) => listed.has(id) && !selected.has(id)).length;
  const changed = added > 0 || removed > 0;

  const options = employees.map((e) => ({
    value: e.id,
    label: `${e.firstName} ${e.lastName}`,
    sub: e.employeeCode,
  }));

  async function save() {
    if (!goal) return;
    setSaving(true);
    setError("");
    try {
      const res = await api.put<{ added: number; removed: number }>(`/org-goals/${goal.id}/assignments`, {
        // People on the goal who are not in the picker are sent back unchanged.
        employeeIds: [...Array.from(selected), ...assignedIds.filter((id) => !listed.has(id))],
      });
      const { added: a, removed: r } = res.data;
      const parts = [
        a > 0 ? `assigned ${a} employee${a === 1 ? "" : "s"}` : "",
        r > 0 ? `unassigned ${r}` : "",
      ].filter(Boolean);
      toast.show(parts.length ? parts.join(", ").replace(/^./, (c) => c.toUpperCase()) : "No changes", "success");
      onSaved();
      onClose();
    } catch (e) {
      logError("OrgGoalsPanel.syncAssignments", e);
      setError(errMsg(e) ?? "Could not save the assignments");
    } finally {
      setSaving(false);
    }
  }

  const count = selected.size;

  return (
    <Dialog open={goal != null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Assign: {goal?.title}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <Field label="Employees" hint={isHR ? undefined : "You can assign goals to your direct reports."}>
            <CheckboxList
              options={options}
              selected={selected}
              onSelectedChange={setSelected}
              searchPlaceholder="Search by name or employee ID"
              emptyText={isHR ? "No employee matches." : "No one on your team matches."}
            />
          </Field>

          {error && <p className="text-[13px] text-[var(--color-error-fg)]">{error}</p>}
        </div>
        <DialogFooter>
          <span className="mr-auto self-center text-[12px] text-surface-500">
            {count === 0 ? "Nobody selected" : `${count} selected`}
            {added > 0 && ` · ${added} to assign`}
            {removed > 0 && ` · ${removed} to unassign`}
          </span>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={saving || !changed} onClick={() => void save()}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// The Assigned to cell: just the head count, which opens the full list.
function AssigneeCount({ goal, onOpen }: { goal: OrgGoal; onOpen: () => void }) {
  const count = goal.assignments.length;
  if (count === 0) return <span className="text-[12px] text-surface-400">Not assigned</span>;
  return (
    <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2.5 text-[12px]" onClick={onOpen}>
      <Users size={13} />
      {count} employee{count === 1 ? "" : "s"}
    </Button>
  );
}

function AssigneesDialog({
  goal,
  canEdit,
  canUnassign,
  onClose,
  onAssignMore,
  onChanged,
}: {
  goal: OrgGoal | null;
  canEdit: boolean;
  canUnassign: (employeeId: string) => boolean;
  onClose: () => void;
  onAssignMore: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  // Two-step unassign per row: the first click arms it, the second confirms.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  useEffect(() => {
    setConfirmingId(null);
  }, [goal?.id]);

  async function unassign(assignmentId: string, name: string) {
    setRemovingId(assignmentId);
    try {
      await api.delete(`/org-goal-assignments/${assignmentId}`);
      toast.show(`${name} unassigned`, "success");
      setConfirmingId(null);
      onChanged();
    } catch (e) {
      logError("OrgGoalsPanel.unassign", e);
      toast.show(errMsg(e) ?? "Could not unassign", "error");
    } finally {
      setRemovingId(null);
    }
  }

  const assignments = goal?.assignments ?? [];

  return (
    <Dialog open={goal != null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Assigned to: {goal?.title}</DialogTitle>
        </DialogHeader>

        {assignments.length === 0 ? (
          <EmptyState message="Nobody is assigned this goal any more." />
        ) : (
          <div className="overflow-x-auto rounded-md border border-surface-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-3">Employee</TableHead>
                  <TableHead>Employee ID</TableHead>
                  <TableHead>Review status</TableHead>
                  {canEdit && <TableHead className="pr-3 text-center">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignments.map((a) => {
                  const name = `${a.employee.firstName} ${a.employee.lastName}`;
                  return (
                    <TableRow key={a.id}>
                      <TableCell className="pl-3 font-medium">{name}</TableCell>
                      <TableCell className="text-surface-500">{a.employee.employeeCode}</TableCell>
                      <TableCell>
                        <AssignmentStatusBadge status={a.status} />
                      </TableCell>
                      {canEdit && (
                        <TableCell className="pr-3 text-center">
                          {!canUnassign(a.employeeId) ? (
                            <span className="text-[12px] text-surface-400">—</span>
                          ) : confirmingId === a.id ? (
                            <div className="flex justify-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => setConfirmingId(null)}>
                                Cancel
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                disabled={removingId === a.id}
                                onClick={() => void unassign(a.id, name)}
                              >
                                {removingId === a.id ? "Removing…" : "Confirm"}
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-[var(--color-error-fg)]"
                              onClick={() => setConfirmingId(a.id)}
                            >
                              <UserMinus size={13} /> Unassign
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <DialogFooter>
          {canEdit && (
            <Button variant="outline" className="mr-auto" onClick={onAssignMore}>
              <UserPlus size={14} /> Assign more
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
